/**
 * Bug Service
 * Handles bug-related database operations
 */

import { BaseService } from './BaseService.js'
import { COLLECTIONS, SCHEMAS } from '../config.js'

export class BugService extends BaseService {
  constructor() {
    super(COLLECTIONS.BUGS)
  }

  /**
   * Get the next available bug ID
   * @returns {Promise<string>} Next bug ID (e.g., B00001)
   */
  async getNextBugId() {
    try {
      // Get all bugs sorted by creation date
      const allBugs = await this.getAll()
      
      // Extract existing bug IDs and find the highest number
      const existingIds = allBugs
        .map(bug => bug.bugId)
        .filter(id => id && id.startsWith('B'))
        .map(id => {
          const match = id.match(/^B(\d+)$/)
          return match ? parseInt(match[1], 10) : 0
        })
        .filter(num => !isNaN(num))
      
      // Find the next available ID
      let nextId = 1
      if (existingIds.length > 0) {
        const maxId = Math.max(...existingIds)
        nextId = maxId + 1
      }
      
      // Format as B00001, B00002, etc.
      return `B${nextId.toString().padStart(5, '0')}`
    } catch (error) {
      // Fallback to timestamp-based ID if there's an error
      return `B${Date.now().toString().slice(-5)}`
    }
  }

  /**
   * Create a new bug
   * @param {Object} bugData - Bug data
   * @returns {Promise<Object>} Created bug
   */
  async createBug(bugData) {
    // Set timestamps first (required by validation)
    const now = new Date()
    if (!bugData.created_at) {
      bugData.created_at = now
    }
    if (!bugData.updated_at) {
      bugData.updated_at = now
    }
    
    // Create a schema for creation (exclude auto-generated fields like 'id')
    const createSchema = { ...SCHEMAS[COLLECTIONS.BUGS] }
    delete createSchema.id // id is auto-generated
    
    const validation = this.validateData(bugData, createSchema)
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`)
    }

    // Set default values if not provided
    if (!bugData.status) {
      bugData.status = 'open'
    }
    if (!bugData.priority) {
      bugData.priority = 'medium'
    }
    if (!bugData.severity) {
      bugData.severity = 'moderate'
    }
    if (!bugData.type) {
      bugData.type = 'bug'
    }
    if (!bugData.attachments) {
      bugData.attachments = ''
    }

    // Initialize comments array if not provided
    if (!bugData.comments) {
      bugData.comments = []
    }

    // Initialize activities array with creation activity
    if (!bugData.activities) {
      bugData.activities = []
    }
    
    bugData.activities.push({
      id: Date.now() + Math.random(),
      type: 'created',
      description: 'Bug created',
      user_id: bugData.created_by,
      timestamp: new Date(),
      changes: {}
    })

    // Transform data for backend API
    // Backend expects steps_to_reproduce and attachments as arrays
    // steps_to_reproduce is already an array from CreateBug.vue
    const apiData = {
      ...bugData,
      steps_to_reproduce: Array.isArray(bugData.steps_to_reproduce) 
        ? bugData.steps_to_reproduce 
        : (bugData.steps_to_reproduce ? [bugData.steps_to_reproduce] : []),
      attachments: bugData.attachments ? bugData.attachments.split('\n').filter(a => a.trim()) : []
    }

    return await this.create(apiData)
  }

  /**
   * Update bug
   * @param {string} bugId - Bug ID
   * @param {Object} updateData - Update data
   * @param {string} userId - User making the update
   * @returns {Promise<Object>} Updated bug
   */
  async updateBug(bugId, updateData, userId = null) {
    // Get current bug to track changes
    const currentBug = await this.getById(bugId)
    if (!currentBug) {
      throw new Error('Bug not found')
    }

    // Transform data for backend API
    // Backend expects steps_to_reproduce and attachments as arrays
    const apiData = { ...updateData }
    if (apiData.steps_to_reproduce !== undefined) {
      if (typeof apiData.steps_to_reproduce === 'string') {
        apiData.steps_to_reproduce = apiData.steps_to_reproduce.split('\n').filter(step => step.trim())
      }
    }
    if (apiData.attachments !== undefined) {
      // Convert attachments from string to array for backend
      if (typeof apiData.attachments === 'string') {
        apiData.attachments = apiData.attachments.split('\n').filter(att => att.trim())
      } else if (Array.isArray(apiData.attachments)) {
        // Already an array, ensure it's an array of strings
        apiData.attachments = apiData.attachments.filter(att => att && att.trim && att.trim().length > 0)
      }
    }

    // Create update schema (only validate fields being updated, not required fields)
    // Use original updateData for validation (string format for frontend schema)
    const updateSchema = {}
    Object.keys(updateData).forEach(key => {
      if (SCHEMAS[COLLECTIONS.BUGS][key]) {
        // Copy the field schema but make it optional for updates
        updateSchema[key] = { ...SCHEMAS[COLLECTIONS.BUGS][key] }
        delete updateSchema[key].required
      }
    })

    const validation = this.validateData(updateData, updateSchema)
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`)
    }

    // Track what changed
    const changes: Record<string, { label: string; from: any; to: any }> = {}
    const fieldLabels = {
      title: 'Title',
      status: 'Status',
      priority: 'Priority',
      severity: 'Severity',
      type: 'Type',
      environment: 'Environment',
      assigned_to: 'Assignee',
      test_case_id: 'Linked Test Case',
      steps_to_reproduce: 'Steps to Reproduce',
      expected_result: 'Expected Result',
      actual_result: 'Actual Result',
      test_data: 'Test Data',
      attachments: 'Attachments'
    }

    Object.keys(updateData).forEach(key => {
      if (fieldLabels[key] && currentBug[key] !== updateData[key]) {
        changes[key] = {
          label: fieldLabels[key],
          from: currentBug[key],
          to: updateData[key]
        }
      }
    })

    // Add activity if there are changes
    if (Object.keys(changes).length > 0 && userId) {
      // CRITICAL: Filter out corrupted activities (empty arrays or invalid objects)
      // This cleans up any corrupted data from previous bugs
      const rawActivities = currentBug.activities || []
      
      // Create a PLAIN JavaScript array (not Vue reactive) to avoid serialization issues
      const activities = []
      for (const activity of rawActivities) {
        // Keep only valid activity objects (not empty arrays or corrupted data)
        if (!activity || typeof activity !== 'object') continue
        if (Array.isArray(activity)) continue // Skip arrays (corrupted data)
        if (!activity.type && !activity.id && !activity.timestamp) continue // Skip empty objects
        
        // Deep clone to plain object (remove Vue reactivity)
        activities.push(JSON.parse(JSON.stringify(activity)))
      }
      const now = new Date()
      
      // Get user name from updateData if provided, otherwise use 'Unknown User'
      const userName = updateData.updated_by_name || 'Unknown User'
      
      // Helper to format field names for display
      const formatFieldName = (field) => {
        return fieldLabels[field] || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
          }
      
      // Helper to format values for display
      const formatValue = (value, field) => {
        if (value === null || value === undefined || value === '') return 'Not set'
        if (typeof value === 'object' && !Array.isArray(value)) return JSON.stringify(value)
        if (Array.isArray(value)) return value.join(', ') || 'None'
        
        // Format specific enum fields
        if (field === 'priority' || field === 'severity') {
          return String(value).charAt(0).toUpperCase() + String(value).slice(1)
        }
        if (field === 'status' || field === 'type') {
          return String(value).split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
        }
        
        return String(value)
      }
      
      // Create individual field_updated activities for each changed field
      Object.entries(changes).forEach(([key, change]) => {
        const oldValue = formatValue(change.from, key)
        const newValue = formatValue(change.to, key)
      
        // Create plain object (not reactive)
        const newActivity = {
        id: Date.now() + Math.random(),
          type: 'field_updated',
          title: 'Field Updated',
          description: `${formatFieldName(key)} changed from "${oldValue}" to "${newValue}"`,
        user_id: userId,
          user_name: userName,
          timestamp: now.toISOString(), // Convert to string for serialization
          details: {
            field: key,
            oldValue: change.from,
            newValue: change.to
          }
        }
        
        activities.push(newActivity)
      })
      
      apiData.activities = activities
    }

    // Use apiData (with array format) for backend API call
    return await this.update(bugId, apiData)
  }

  /**
   * Soft delete a bug
   * @param {string} bugId - Bug ID
   * @param {string} deletedBy - User ID who deleted the bug
   * @returns {Promise<Object>} Updated bug
   */
  async softDelete(bugId, deletedBy, deletedByName = null) {
    try {
      // Use dedicated soft-delete endpoint (bypasses update() safeguard)
      const { http } = await import('@/utils/httpClient')
      
      // Get userName from authStore if not provided
      let userName = deletedByName
      if (!userName) {
        try {
          const { useAuthStore } = await import('@/stores/auth')
          const authStore = useAuthStore()
          userName = authStore.user?.name || authStore.user?.email || 'Unknown'
        } catch {
          userName = 'Unknown'
        }
      }
      
      const response = await http.post(`/bugs/${bugId}/soft-delete`, {
        deletedBy,
        deletedByName: userName
      })
      return response
    } catch (error) {
      console.error('❌ Error soft deleting bug:', error)
      throw error
    }
  }

  /**
   * Restore a soft-deleted bug
   * @param {string} bugId - Bug ID
   * @param {string} restoredBy - User ID who restored the bug
   * @returns {Promise<Object>} Updated bug
   */
  async restore(bugId, restoredBy = null) {
    // Get current bug to add activity
    const currentBug = await this.getById(bugId)
    if (!currentBug) {
      throw new Error('Bug not found')
    }

    // Add restore activity
    const activities = currentBug.activities || []
    if (restoredBy) {
      activities.push({
        id: Date.now() + Math.random(),
        type: 'restored',
        description: 'Bug restored',
        user_id: restoredBy,
        timestamp: new Date(),
        changes: {}
      })
    }

    const updateData = {
      is_deleted: false,
      deleted_at: null,
      deleted_by: null,
      activities: activities
    }
    return await this.update(bugId, updateData)
  }

  /**
   * Soft delete multiple bugs in batch
   * @param {Array<string>} bugIds - Array of bug IDs to delete
   * @param {string} deletedBy - User ID who deleted the bugs
   * @returns {Promise<Array>} Array of operation results
   */
  async bulkSoftDelete(bugIds, deletedBy) {
    try {
      await this._ensureDatabaseService()
      
      // Get all bugs to be deleted
      const bugs = await Promise.all(
        bugIds.map(id => this.getById(id))
      )
      
      // Prepare batch operations
      const operations = bugs.map(bug => {
        if (!bug) return null
        
        const activities = bug.activities || []
        activities.push({
          id: Date.now() + Math.random(),
          type: 'deleted',
          description: 'Bug deleted',
          user_id: deletedBy,
          timestamp: new Date(),
          changes: {}
        })
        
        return {
          type: 'update',
          collection: this.collectionName,
          id: bug.id,
          data: {
            is_deleted: true,
            deleted_at: new Date(),
            deleted_by: deletedBy,
            activities: activities,
            updated_at: new Date()
          }
        }
      }).filter(op => op !== null)
      
      // Execute batch operations
      const results = await this.batch(operations)
      
      return results || []
    } catch (error) {
      throw error
    }
  }

  /**
   * Update multiple bugs in batch using optimized batch-update endpoint
   * @param {Array<string>} bugIds - Array of bug IDs to update
   * @param {Object} updateData - Data to update
   * @returns {Promise<Object>} Result with success and failed counts
   */
  async batchUpdate(bugIds, updateData) {
    try {
      if (!bugIds || bugIds.length === 0) {
        return { success: 0, failed: 0 }
      }

      const { http } = await import('@/utils/httpClient')
      const response = await http.post('/bugs/batch-update', {
        bugIds,
        updateData
      })
      
      return response
    } catch (error) {
      console.error('❌ Error batch updating bugs:', error)
      throw error
    }
  }

  /**
   * Restore multiple bugs in batch
   * @param {Array<string>} bugIds - Array of bug IDs to restore
   * @param {string} restoredBy - User ID who restored the bugs
   * @returns {Promise<Array>} Array of operation results
   */
  async bulkRestore(bugIds, restoredBy) {
    try {
      await this._ensureDatabaseService()
      
      // Get all bugs to be restored
      const bugs = await Promise.all(
        bugIds.map(id => this.getById(id))
      )
      
      // Prepare batch operations
      const operations = bugs.map(bug => {
        if (!bug) return null
        
        const activities = bug.activities || []
        if (restoredBy) {
          activities.push({
            id: Date.now() + Math.random(),
            type: 'restored',
            description: 'Bug restored',
            user_id: restoredBy,
            timestamp: new Date(),
            changes: {}
          })
        }
        
        return {
          type: 'update',
          collection: this.collectionName,
          id: bug.id,
          data: {
            is_deleted: false,
            deleted_at: null,
            deleted_by: null,
            activities: activities,
            updated_at: new Date()
          }
        }
      }).filter(op => op !== null)
      
      // Execute batch operations
      const results = await this.batch(operations)
      
      return results || []
    } catch (error) {
      throw error
    }
  }

  /**
   * Clone a bug
   * @param {string} bugId - Bug ID to clone
   * @param {string} clonedBy - User ID who cloned the bug
   * @returns {Promise<Object>} Cloned bug
   */
  async cloneBug(bugId, clonedBy = null) {
    const originalBug = await this.getById(bugId)
    if (!originalBug) {
      throw new Error('Bug not found')
    }

    // Generate new bug ID
    const newBugId = await this.getNextBugId()

    // Convert attachments to string format if it's an array
    let attachmentsString = ''
    if (originalBug.attachments) {
      if (Array.isArray(originalBug.attachments)) {
        // Convert array to newline-separated string
        attachmentsString = originalBug.attachments
          .filter(att => att && (typeof att === 'string' ? att.trim() : att.url || att.name))
          .map(att => typeof att === 'string' ? att : (att.url || att.name || ''))
          .filter(att => att.trim())
          .join('\n')
      } else if (typeof originalBug.attachments === 'string') {
        attachmentsString = originalBug.attachments
      }
    }

    // Ensure steps_to_reproduce is an array
    let stepsArray = []
    if (originalBug.steps_to_reproduce) {
      if (Array.isArray(originalBug.steps_to_reproduce)) {
        stepsArray = originalBug.steps_to_reproduce
      } else if (typeof originalBug.steps_to_reproduce === 'string') {
        stepsArray = originalBug.steps_to_reproduce.split('\n').filter(step => step.trim())
      }
    }

    // Create clone data (exclude id-specific fields)
    // Note: createBug will handle comments, activities, and timestamps automatically
    const clonedData = {
      bugId: newBugId,
      title: `${originalBug.title} (Copy)`,
      description: originalBug.description,
      status: 'open', // Reset status for clone
      priority: originalBug.priority,
      severity: originalBug.severity,
      type: originalBug.type,
      project_id: originalBug.project_id,
      assigned_to: originalBug.assigned_to,
      environment: originalBug.environment,
      browser: originalBug.browser,
      os: originalBug.os,
      team_id: originalBug.team_id,
      feature_id: originalBug.feature_id,
      feature_ids: originalBug.feature_ids || [],
      test_case_ids: originalBug.test_case_ids || [],
      test_run_id: originalBug.test_run_id,
      steps_to_reproduce: stepsArray,
      expected_result: originalBug.expected_result,
      actual_result: originalBug.actual_result,
      test_data: originalBug.test_data || '',
      attachments: attachmentsString, // Use string format (createBug will convert to array for API)
      created_by: clonedBy,
      created_by_name: originalBug.created_by_name
    }

    return await this.createBug(clonedData)
  }

  /**
   * Get bugs by project
   * @param {string} projectId - Project ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of bugs
   */
  async getByProject(projectId, options = {}) {
    // Filter out deleted bugs by default
    const bugs = await this.findBy('project_id', projectId, options)
    return bugs.filter(bug => !bug.is_deleted)
  }

  /**
   * Get deleted bugs by project
   * @param {string} projectId - Project ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of deleted bugs
   */
  async getDeletedByProject(projectId, options = {}) {
    const bugs = await this.findBy('project_id', projectId, options)
    return bugs.filter(bug => bug.is_deleted)
  }

  /**
   * Get bugs by status
   * @param {string} status - Bug status
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of bugs
   */
  async getByStatus(status, options = {}) {
    return await this.findBy('status', status, options)
  }

  /**
   * Get bugs by priority
   * @param {string} priority - Bug priority
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of bugs
   */
  async getByPriority(priority, options = {}) {
    return await this.findBy('priority', priority, options)
  }

  /**
   * Get bugs by severity
   * @param {string} severity - Bug severity
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of bugs
   */
  async getBySeverity(severity, options = {}) {
    return await this.findBy('severity', severity, options)
  }

  /**
   * Get bugs by type
   * @param {string} type - Bug type
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of bugs
   */
  async getByType(type, options = {}) {
    return await this.findBy('type', type, options)
  }

  /**
   * Get bugs by assigned user
   * @param {string} userId - User ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of bugs
   */
  async getByAssignedUser(userId, options = {}) {
    return await this.findBy('assigned_to', userId, options)
  }

  /**
   * Get bugs by creator
   * @param {string} userId - User ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of bugs
   */
  async getByCreator(userId, options = {}) {
    return await this.findBy('created_by', userId, options)
  }

  /**
   * Get bugs by test case
   * @param {string} testCaseId - Test case ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of bugs
   */
  async getByTestCase(testCaseId, options = {}) {
    return await this.findBy('test_case_id', testCaseId, options)
  }

  /**
   * Get bugs by test run
   * @param {string} testRunId - Test run ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of bugs
   */
  async getByTestRun(testRunId, options = {}) {
    return await this.findBy('test_run_id', testRunId, options)
  }

  /**
   * Get open bugs
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of open bugs
   */
  async getOpen(options = {}) {
    return await this.getByStatus('open', options)
  }

  /**
   * Get closed bugs
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of closed bugs
   */
  async getClosed(options = {}) {
    return await this.getByStatus('closed', options)
  }

  /**
   * Get in-progress bugs
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of in-progress bugs
   */
  async getInProgress(options = {}) {
    return await this.getByStatus('in_progress', options)
  }

  /**
   * Close bug
   * @param {string} bugId - Bug ID
   * @param {string} resolution - Resolution reason (optional)
   * @returns {Promise<Object>} Updated bug
   */
  async close(bugId, resolution = null) {
    const updateData = {
      status: 'closed',
      resolution: resolution
    }
    return await this.update(bugId, updateData)
  }

  /**
   * Reopen bug
   * @param {string} bugId - Bug ID
   * @returns {Promise<Object>} Updated bug
   */
  async reopen(bugId) {
    const updateData = {
      status: 'open',
      resolution: null
    }
    return await this.update(bugId, updateData)
  }

  /**
   * Assign bug
   * @param {string} bugId - Bug ID
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Updated bug
   */
  async assign(bugId, userId) {
    return await this.update(bugId, { assigned_to: userId })
  }

  /**
   * Unassign bug
   * @param {string} bugId - Bug ID
   * @returns {Promise<Object>} Updated bug
   */
  async unassign(bugId) {
    return await this.update(bugId, { assigned_to: null })
  }

  /**
   * Search bugs
   * @param {string} searchTerm - Search term
   * @param {Object} options - Search options
   * @returns {Promise<Array>} Array of matching bugs
   */
  async searchBugs(searchTerm, options = {}) {
    const searchOptions = {
      fields: ['title', 'description', 'steps_to_reproduce', 'expected_behavior', 'actual_behavior'],
      ...options
    }
    return await this.search(searchTerm, searchOptions)
  }

  /**
   * Get bug statistics
   * @param {string} projectId - Project ID (optional)
   * @returns {Promise<Object>} Bug statistics
   */
  async getStatistics(projectId = null) {
    let bugs
    if (projectId) {
      bugs = await this.getByProject(projectId)
    } else {
      bugs = await this.getAll()
    }

    const statusCounts = {}
    const priorityCounts = {}
    const severityCounts = {}
    const typeCounts = {}
    const environmentCounts = {}

    bugs.forEach(bug => {
      // Count by status
      statusCounts[bug.status] = (statusCounts[bug.status] || 0) + 1

      // Count by priority
      priorityCounts[bug.priority] = (priorityCounts[bug.priority] || 0) + 1

      // Count by severity
      severityCounts[bug.severity] = (severityCounts[bug.severity] || 0) + 1

      // Count by type
      typeCounts[bug.type] = (typeCounts[bug.type] || 0) + 1

      // Count by environment
      if (bug.environment) {
        environmentCounts[bug.environment] = (environmentCounts[bug.environment] || 0) + 1
      }
    })

    return {
      total: bugs.length,
      statusCounts,
      priorityCounts,
      severityCounts,
      typeCounts,
      environmentCounts,
      lastUpdated: new Date()
    }
  }

  /**
   * Get bug trends
   * @param {string} projectId - Project ID (optional)
   * @param {number} days - Number of days to look back
   * @returns {Promise<Array>} Array of trend data
   */
  async getTrends(projectId = null, days = 30) {
    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - days)

    const conditions = [
      this._where('created_at', '>=', cutoffDate)
    ]

    if (projectId) {
      conditions.push(this._where('project_id', '==', projectId))
    }

    const bugs = await this.findWhere(conditions, {
      orderBy: [this._orderBy('created_at', 'asc')]
    })

    // Group by date
    const trends = {}
    bugs.forEach(bug => {
      const date = new Date(bug.created_at).toISOString().split('T')[0]
      if (!trends[date]) {
        trends[date] = { date, total: 0, open: 0, closed: 0, in_progress: 0 }
      }
      trends[date].total++
      trends[date][bug.status] = (trends[date][bug.status] || 0) + 1
    })

    return Object.values(trends)
  }

  /**
   * Get recent bugs
   * @param {number} limit - Number of recent bugs to return
   * @param {string} projectId - Project ID (optional)
   * @returns {Promise<Array>} Array of recent bugs
   */
  async getRecent(limit = 10, projectId = null) {
    const options = {
      orderBy: [this._orderBy('created_at', 'desc')],
      limit
    }

    if (projectId) {
      return await this.getByProject(projectId, options)
    } else {
      return await this.getAll(options)
    }
  }

  /**
   * Add an activity to a bug
   * @param {string} bugId - Bug ID
   * @param {Object} activityData - Activity data
   * @returns {Promise<Object>} Updated bug with new activity
   */
  async addActivity(bugId, activityData) {
    try {
      const bug = await this.getById(bugId)
      if (!bug) {
        throw new Error('Bug not found')
      }
      
      const activities = bug.activities || []
      activities.push({
        id: Date.now() + Math.random(),
        ...activityData,
        timestamp: activityData.timestamp || new Date()
      })
      
      return await this.update(bugId, { activities })
    } catch (error) {
      throw error
    }
  }

  /**
   * Add a comment to a bug
   * @param {string} bugId - Bug ID
   * @param {string} userId - User ID who is adding the comment
   * @param {string} userName - User name who is adding the comment
   * @param {string} commentText - Comment text
   * @returns {Promise<Object>} Updated bug with new comment
   */
  async addComment(bugId, userId, userName, commentText) {
    try {
      // Get the current bug
      const bug = await this.getById(bugId)
      if (!bug) {
        throw new Error('Bug not found')
      }
      
      // Create new comment object      
      const newComment = {
        id: `comment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        user_id: userId,
        user_name: userName,
        comment: commentText,
        timestamp: new Date(),
        created_at: new Date()
      }
      
      // Add comment to the comments array (ensure it exists)
      const comments = Array.isArray(bug.comments) ? [...bug.comments] : []
      comments.push(newComment)
      
      // Add activity for comment
      const activities = Array.isArray(bug.activities) ? [...bug.activities] : []
      activities.push({
        type: 'comment_added',
        description: `${userName} added a comment`,
        userName: userName,
        user_name: userName,
        userId: userId,
        user_id: userId,
        timestamp: new Date(),
        created_at: new Date(),
        details: {
          commentId: newComment.id,
          commentPreview: commentText.substring(0, 100)
        }
      })
      
      // Update the bug with new comment and activity in a single update
      await this.update(bugId, {
        comments: comments,
        activities: activities,
        updated_at: new Date()
      })
      
      return await this.getById(bugId)
    } catch (error) {
      throw error
    }
  }

  /**
   * Get all comments for a bug
   * @param {string} bugId - Bug ID
   * @returns {Promise<Array>} Array of comments
   */
  async getComments(bugId) {
    try {
      const bug = await this.getById(bugId)
      if (!bug) {
        throw new Error('Bug not found')
      }
      
      // Return comments array, sorted by timestamp (oldest first - reverse order like ClickUp)
      const comments = bug.comments || []
      return comments.sort((a, b) => {
        const dateA = new Date(a.timestamp || a.created_at).getTime()
        const dateB = new Date(b.timestamp || b.created_at).getTime()
        return dateA - dateB
      })
    } catch (error) {
      throw error
    }
  }

  /**
   * Delete a comment from a bug
   * @param {string} bugId - Bug ID
   * @param {string} commentId - Comment ID to delete
   * @param {string} userId - User ID who is deleting the comment
   * @returns {Promise<Object>} Updated bug without the comment
   */
  async deleteComment(bugId, commentId, userId) {
    try {
      
      const bug = await this.getById(bugId)
      if (!bug) {
        throw new Error('Bug not found')
      }
      
      // Filter out the comment
      const comments = (bug.comments || []).filter(comment => comment.id !== commentId)
      
      // Update the bug
      const updateData = {
        comments: comments,
        updated_at: new Date()
      }
      
      const updatedBug = await this.update(bugId, updateData)
      
      return updatedBug
    } catch (error) {
      throw error
    }
  }

  /**
   * Update a comment on a bug
   * @param {string} bugId - Bug ID
   * @param {string} commentId - Comment ID to update
   * @param {string} newCommentText - New comment text
   * @returns {Promise<Object>} Updated bug with edited comment
   */
  async updateComment(bugId, commentId, newCommentText) {
    try {
      
      const bug = await this.getById(bugId)
      if (!bug) {
        throw new Error('Bug not found')
      }
      
      // Find and update the comment
      const comments = bug.comments || []
      const commentIndex = comments.findIndex(comment => comment.id === commentId)
      
      if (commentIndex === -1) {
        throw new Error('Comment not found')
      }
      
      comments[commentIndex].comment = newCommentText
      comments[commentIndex].edited_at = new Date()
      comments[commentIndex].is_edited = true
      
      // Update the bug
      const updateData = {
        comments: comments,
        updated_at: new Date()
      }
      
      const updatedBug = await this.update(bugId, updateData)
      
      return updatedBug
    } catch (error) {
      throw error
    }
  }
}

export const bugService = new BugService()
